<script setup lang="ts">
import { Modal } from 'ant-design-vue'
import { FolderOutput, Inbox } from 'lucide-vue-next'
import type { Folder } from '../models/Folder'

const props = defineProps<{
  visible: boolean
  folders: Folder[]
  currentFolderId: string | null
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  'folder-chosen': [folderId: string | null]
}>()

function handleFolderClick(folderId: string | null) {
  emit('folder-chosen', folderId)
  emit('update:visible', false)
}

function handleCancel() {
  emit('update:visible', false)
}

function isDisabled(folderId: string | null): boolean {
  return folderId === props.currentFolderId
}
</script>

<template>
  <Modal
    :open="visible"
    title="Move to Folder"
    :footer="null"
    :maskClosable="true"
    width="400px"
    @cancel="handleCancel"
    data-testid="folder-picker-dialog"
  >
    <div class="folder-picker-list" role="listbox" aria-label="Select destination folder">
      <!-- Remove from folder option -->
      <div
        class="folder-picker-item"
        :class="{
          'folder-picker-item--disabled': isDisabled(null),
        }"
        role="option"
        :aria-selected="isDisabled(null)"
        :aria-disabled="isDisabled(null)"
        :tabindex="isDisabled(null) ? -1 : 0"
        @click="!isDisabled(null) && handleFolderClick(null)"
        @keydown.enter="!isDisabled(null) && handleFolderClick(null)"
        @keydown.space.prevent="!isDisabled(null) && handleFolderClick(null)"
        data-testid="folder-picker-remove"
      >
        <Inbox class="folder-picker-item__icon" :size="16" />
        <span class="folder-picker-item__label">Remove from folder</span>
      </div>

      <!-- Separator -->
      <div v-if="folders.length > 0" class="folder-picker-separator" />

      <!-- Folder list -->
      <div
        v-for="folder in folders"
        :key="folder.id"
        class="folder-picker-item"
        :class="{
          'folder-picker-item--disabled': isDisabled(folder.id),
        }"
        role="option"
        :aria-selected="isDisabled(folder.id)"
        :aria-disabled="isDisabled(folder.id)"
        :tabindex="isDisabled(folder.id) ? -1 : 0"
        @click="!isDisabled(folder.id) && handleFolderClick(folder.id)"
        @keydown.enter="!isDisabled(folder.id) && handleFolderClick(folder.id)"
        @keydown.space.prevent="!isDisabled(folder.id) && handleFolderClick(folder.id)"
        :data-testid="`folder-picker-item-${folder.id}`"
      >
        <span
          v-if="folder.color"
          class="folder-picker-item__color-dot"
          :style="{ backgroundColor: folder.color }"
        />
        <FolderOutput class="folder-picker-item__icon" :size="16" />
        <span class="folder-picker-item__label">{{ folder.name }}</span>
        <span v-if="isDisabled(folder.id)" class="folder-picker-item__current">
          (current)
        </span>
      </div>

      <!-- Empty state -->
      <div v-if="folders.length === 0" class="folder-picker-empty">
        No folders available. Create a folder first.
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.folder-picker-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 0;
  max-height: 400px;
  overflow-y: auto;
}

.folder-picker-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.folder-picker-item:hover:not(.folder-picker-item--disabled) {
  background-color: var(--color-background-soft, #f5f5f5);
}

.folder-picker-item:focus-visible {
  outline: 2px solid #1677ff;
  outline-offset: -2px;
}

.folder-picker-item--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.folder-picker-item__icon {
  flex-shrink: 0;
  color: var(--color-text-secondary, #666666);
}

.folder-picker-item__label {
  font-size: 14px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.folder-picker-item__color-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.folder-picker-item__current {
  font-size: 12px;
  color: var(--color-text-secondary, #999999);
  margin-left: auto;
}

.folder-picker-separator {
  height: 1px;
  background-color: var(--color-border, #e8e8e8);
  margin: 4px 0;
}

.folder-picker-empty {
  padding: 16px 12px;
  text-align: center;
  font-size: 13px;
  color: var(--color-text-secondary, #999999);
}
</style>
